/* eslint-disable i18next/no-literal-string */
import * as React from "react";
import { useRef } from "react";
import { Link } from "~/components/ui/link";
import { cn } from "~/lib/utils";
import { useHasBeenOnScreen } from "~/lib/use-has-been-on-screen";
import { LandingPageHeaderH2 } from "~/components/landing/LandingText";
import { Picture } from "~/components/ui/picture";
import { imageUrl } from "~/lib/image-url";

// Plain public/ string paths, not JS imports — see the comment on bgImageSet in
// app/components/home/UseCases.tsx for why.
const womenDriving = imageUrl("/images/landing/women-driving.jpg");
const beachFamily = imageUrl("/images/landing/beach-family.jpg");

/**
 * Ported from bouncieweb's `LandingPageComponents/Reviews.js`.
 *
 * Two press-review cards (Car & Driver and Forbes) in a responsive grid. Each
 * card's image slowly scales up (1.0 -> 1.1 over 8s) the first time it scrolls
 * into view, driven by `useHasBeenOnScreen`. The scale keyframe from the
 * original Emotion `keyframes` is reproduced in a component-local <style> tag
 * (the new project's shared app.css is owned elsewhere).
 *
 * The default images are the copied `women-driving.jpg` (Car & Driver) and
 * `beach-family.jpg` (Forbes); the originals were Next static imports.
 *
 * Original breakpoint map (theme/mediaQueries):
 *   xsm 431px -> min-[431px]:  sm 640px -> sm:  md 768px -> md:  lg 1024px -> lg:
 */

// borderRadius from bouncieweb's Bento components (20px)
const BORDER_RADIUS = 20;

/** Ported from `CarAndDriverLogo` -> `WrappedSVGs/CarAndDriverLogo`. */
function CarAndDriverLogo({ width = 258 }: { width?: number }) {
  return (
    <div className="align-middle">
      <svg
        width={width}
        height={width * (40 / 258)}
        viewBox="0 0 258 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M14.5305 30.0488C14.5305 31.2444 14.1428 32.0762 12.8513 32.0762C11.4956 32.0762 11.0433 31.3481 11.0433 30.0488V9.40973C11.0433 8.0581 11.4306 7.48624 12.7871 7.48624C14.1428 7.48624 14.5305 8.16228 14.5305 9.40973V15.3881H25.0569V9.04532C25.0569 3.95103 22.2158 0 13.6913 0H11.7538C3.09995 0 0 4.36684 0 9.92989V29.5811C0 35.5075 2.84149 39.822 11.8183 39.822H13.6913C22.0864 39.822 25.0569 35.1954 25.0569 30.3081V22.3028H14.5305V30.0488ZM41.1166 39.1983H51.4499L44.7335 0.52H31.6237L25.4886 39.1983H34.7234L35.4338 33.5316H40.342L41.1166 39.1983ZM37.8878 14.1407L39.3732 25.8894H36.4028L37.8878 14.1407ZM63.2281 7.17399H64.7783C66.199 7.17399 66.5219 7.64182 66.5219 8.99392V14.1921C66.5219 15.5442 65.9402 16.0643 64.5844 16.0643H63.2279L63.2281 7.17399ZM63.2281 23.3425H64.7133C66.134 23.3425 66.5219 23.9144 66.5219 25.2138V36.4435C66.5219 37.847 66.909 38.7306 67.3611 39.1983H77.8875V38.9386C77.5648 38.4184 77.2421 37.5865 77.2421 36.131V23.3425C77.2421 20.4306 74.8525 18.8715 73.0445 18.2995C74.9167 17.6234 77.2421 16.012 77.2421 12.4249V7.32973C77.2421 2.75529 74.0133 0.52 69.2986 0.52H52.5727V39.1983H63.2281V23.3425ZM116.319 39.1983H131.623C137.888 39.1983 141.182 36.3389 141.182 30.3081V9.04532C141.182 2.85962 137.501 0.52 131.559 0.52H116.319V39.1983ZM126.845 7.69414H128.459C129.88 7.69414 130.332 8.37019 130.332 9.92989V29.4249C130.332 31.0365 129.88 31.7646 128.33 31.7646H126.845V7.69414ZM153.657 7.17399H155.207C156.628 7.17399 156.95 7.64182 156.95 8.99392V14.1921C156.95 15.5442 156.369 16.0643 155.013 16.0643H153.657V7.17399ZM153.657 23.3425H155.143C156.563 23.3425 156.951 23.9144 156.951 25.2138V36.4435C156.951 37.847 157.338 38.7306 157.79 39.1983H168.317V38.9386C167.994 38.4184 167.671 37.5865 167.671 36.131V23.3425C167.671 20.4306 165.281 18.8715 163.473 18.2995C165.346 17.6234 167.671 16.012 167.671 12.4249V7.32973C167.671 2.75529 164.443 0.52 159.728 0.52H143.002V39.1983H153.657L153.657 23.3425ZM170.053 39.1988H180.709V0.52H170.053V39.1988ZM188.8 39.1983H202.749L209.208 0.52H198.94L196.033 24.746L193.127 0.52H181.891L188.8 39.1983ZM210.389 0.52V39.1983H230.731V31.0365H220.915V23.3425H228.471V15.1284H220.915V8.52578H230.344V0.52H210.389ZM242.859 7.17399H244.409C245.829 7.17399 246.152 7.64182 246.152 8.99392V14.1921C246.152 15.5442 245.571 16.0643 244.215 16.0643H242.859V7.17399ZM242.859 23.3425H244.344C245.765 23.3425 246.152 23.9144 246.152 25.2138V36.4435C246.152 37.847 246.54 38.7306 246.992 39.1983H257.518V38.9386C257.196 38.4184 256.872 37.5865 256.872 36.131V23.3425C256.872 20.4306 254.483 18.8715 252.675 18.2995C254.548 17.6234 256.872 16.012 256.872 12.4249V7.32973C256.872 2.75529 253.644 0.52 248.929 0.52H232.203V39.1983H242.859V23.3425ZM87.2092 27.7828H90.8822L88.4221 11.8614H83.6192L81.3717 27.7828H84.7548L85.0156 25.4497H86.9256L87.2092 27.7828ZM85.9139 17.0163L86.5706 22.485H85.3702L85.9139 17.0163ZM92.0035 11.8614V27.7828H95.3866V19.4794L97.705 27.7827H101.136V11.8614H97.7994V19.565L95.7885 11.8614H92.0035ZM102.845 27.7828H108.452C110.746 27.7828 111.953 26.6053 111.953 24.123V15.3714C111.953 12.8249 110.604 11.8611 108.428 11.8611H102.845V27.7828ZM106.589 14.8151H107.293C107.813 14.8151 107.979 15.0926 107.979 15.7352V23.7595C107.979 24.4227 107.813 24.7223 107.245 24.7223H106.589V14.8151Z"
          fill="#2A3141"
        />
        <path
          d="M80.5308 9.58509H111.944V0.549805H80.531L80.5308 9.58509Z"
          fill="#D2232A"
        />
        <path
          d="M80.5308 39.0946H111.944V30.0601H80.531L80.5308 39.0946Z"
          fill="#0061AF"
        />
      </svg>
    </div>
  );
}

/** Ported from `ForbesLogo` -> `WrappedSVGs/ForbesLogo`. */
function ForbesLogo({
  width = 148,
  color = "#33629A",
}: {
  width?: number;
  color?: string;
}) {
  return (
    <div className="align-middle">
      <svg
        width={width}
        height={width * (38 / 148)}
        viewBox="0 0 148 38"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M83.9264 12.4814C83.9264 8.18515 84.0005 4.18515 84.2227 0.481445L72.8894 2.62959V3.66663L74.0005 3.81478C74.8153 3.88885 75.3338 4.18515 75.6301 4.62959C75.9264 5.14811 76.1486 5.88885 76.2968 6.99996C76.4449 9.14811 76.5931 14.037 76.519 21.7407C76.519 29.3703 76.4449 34.1851 76.2968 36.037C80.3708 36.9259 83.556 37.2963 85.9264 37.2963C90.3708 37.2963 93.8523 36.037 96.3708 33.4444C98.8894 30.9259 100.223 27.3703 100.223 23C100.223 19.5185 99.2597 16.6296 97.3338 14.3333C95.4079 12.037 92.9634 10.9259 90.0745 10.9259C88.1486 10.9259 86.1486 11.4444 83.9264 12.4814ZM84.1486 35.2963C84.0005 32.9259 83.8523 25.8148 83.8523 14.1851C84.519 13.9629 85.4079 13.8148 86.519 13.8148C88.2968 13.8148 89.7042 14.7037 90.7412 16.5555C91.7782 18.4074 92.2968 20.6296 92.2968 23.4444C92.2968 26.9259 91.7042 29.7407 90.519 32.1111C89.3338 34.4074 87.8523 35.5926 86.0005 35.5926C85.4079 35.4444 84.8153 35.3703 84.1486 35.2963ZM30.3708 1.22219H0.741211V2.70367L2.29677 2.85182C3.48195 3.07404 4.29677 3.51848 4.81528 4.18515C5.3338 4.92589 5.6301 6.11107 5.70417 7.7407C6.29677 15.7407 6.29677 23.2222 5.70417 30.1111C5.55603 31.7407 5.25973 32.9259 4.81528 33.6666C4.29677 34.4074 3.48195 34.8518 2.29677 35L0.741211 35.2222V36.7037H19.8523V35.2222L17.8523 35.074C17.3643 35.0332 16.8896 34.894 16.4567 34.6649C16.0239 34.4357 15.6419 34.1214 15.3338 33.7407C14.8153 33 14.519 31.8148 14.4449 30.1851C14.2227 27.2222 14.0745 23.8148 14.0745 20.037L18.0745 20.1111C20.2227 20.1851 21.7042 21.8148 22.4449 25.074H23.9264V13H22.4449C21.7042 16.1851 20.2968 17.8148 18.0745 17.8889L14.0745 17.9629C14.0745 11.2963 14.2227 6.55552 14.4449 3.66663H20.2968C24.4449 3.66663 27.2597 6.3333 28.8894 11.6666L30.6671 11.1481L30.3708 1.22219ZM26.8894 24.037C26.8894 27.8889 28.0005 31.074 30.1486 33.5926C32.2968 36.1111 35.4819 37.2963 39.4819 37.2963C43.4819 37.2963 46.7412 36.037 49.1116 33.4444C51.482 30.9259 52.5931 27.7407 52.5931 23.8889C52.5931 20.037 51.4819 16.8518 49.3338 14.3333C47.1857 11.8148 44.0005 10.6296 40.0005 10.6296C36.0005 10.6296 32.7412 11.8889 30.3708 14.4814C28.0005 17 26.8894 20.1851 26.8894 24.037ZM43.4079 15.5926C44.2968 17.7407 44.6671 20.5555 44.6671 23.9629C44.6671 31.8148 43.0375 35.6666 39.8523 35.6666C38.2227 35.6666 36.9634 34.5555 36.0745 32.3333C35.1857 30.1111 34.8153 27.2963 34.8153 23.9629C34.8153 16.1851 36.4449 12.3333 39.6301 12.3333C41.2597 12.2592 42.519 13.3703 43.4079 15.5926ZM106 33.5926C108.445 36.037 111.556 37.2963 115.26 37.2963C117.556 37.2963 119.556 36.8518 121.334 35.8889C123.112 35 124.519 33.8889 125.482 32.5555L124.741 31.6666C123.112 32.9259 121.26 33.5185 119.112 33.5185C116.149 33.5185 113.852 32.5555 112.297 30.5555C110.667 28.5555 109.926 26.037 110.075 22.7777H125.926C125.926 19.2222 125.038 16.3333 123.186 14.037C121.334 11.8148 118.741 10.7037 115.408 10.7037C111.26 10.7037 108.075 12.037 105.778 14.6296C103.482 17.2222 102.371 20.4074 102.371 24.1851C102.371 28.037 103.556 31.1481 106 33.5926ZM111.482 15C112.297 13.2222 113.334 12.3333 114.741 12.3333C116.963 12.3333 118.075 15.1481 118.075 20.8518L110.223 21C110.297 18.7777 110.667 16.7777 111.482 15ZM145.852 12.037C143.852 11.1481 141.334 10.6296 138.297 10.6296C135.186 10.6296 132.741 11.4444 130.889 13C129.038 14.5555 128.075 16.4814 128.075 18.7777C128.075 20.7777 128.667 22.3333 129.778 23.4444C130.889 24.5555 132.667 25.5185 134.963 26.3333C137.038 27.074 138.519 27.8148 139.26 28.4814C140 29.2222 140.445 30.037 140.445 31.1481C140.445 32.1851 140.075 33.1481 139.26 33.8889C138.519 34.7037 137.482 35.074 136.149 35.074C132.889 35.074 130.445 32.7037 128.741 27.9629L127.482 28.3333L127.778 35.7407C130.445 36.7777 133.408 37.2963 136.667 37.2963C140.075 37.2963 142.667 36.5555 144.593 35C146.519 33.5185 147.482 31.3703 147.482 28.7037C147.482 26.9259 147.038 25.4444 146.075 24.3333C145.112 23.2222 143.556 22.2592 141.334 21.3703C138.889 20.4814 137.186 19.6666 136.297 18.9259C135.408 18.1851 134.963 17.2963 134.963 16.1851C134.963 15.074 135.26 14.1851 135.926 13.4444C136.593 12.7037 137.408 12.4074 138.445 12.4074C141.408 12.4074 143.556 14.5555 144.889 18.7777L146.149 18.4074L145.852 12.037ZM74.5931 11.3703C73.556 10.8518 72.4449 10.6296 71.1857 10.6296C69.9264 10.6296 68.6671 11.1481 67.2597 12.1851C65.8523 13.2222 64.8153 14.6296 64.0005 16.5555L64.0745 10.6296L52.8153 12.8518V13.8889L53.9264 13.9629C55.3338 14.1111 56.1486 15.2222 56.2968 17.2222C56.7412 21.8148 56.7412 26.7037 56.2968 31.8889C56.1486 33.8889 55.3338 34.9259 53.9264 35.1481L52.8153 35.2963V36.7037H68.519V35.2963L66.519 35.1481C65.1116 35 64.2968 33.8889 64.1486 31.8889C63.7042 27.5926 63.6301 23 64.0005 18.2592C64.4449 17.5185 65.4079 17.074 66.8894 16.9259C68.3708 16.7777 70.0745 17.2222 71.8523 18.2592L74.5931 11.3703Z"
          fill={color}
        />
      </svg>
    </div>
  );
}

function Review({
  image,
  imageAlt,
  logo,
  text,
  link,
}: {
  image: string;
  imageAlt: string;
  logo: React.ReactNode;
  text: React.ReactNode;
  link?: string;
}) {
  const imageAreaRef = useRef<HTMLDivElement>(null);
  const hasBeenOnScreen = useHasBeenOnScreen(imageAreaRef);

  return (
    <div>
      {/* ReviewImageArea */}
      <div
        ref={imageAreaRef}
        className={cn(
          "relative mb-4 overflow-hidden",
          "h-[45vw] sm:h-[16rem] md:h-[11rem] lg:h-[15rem]",
        )}
        style={{ borderRadius: BORDER_RADIUS }}
      >
        {/* ReviewImageSizer: scales 1.0 -> 1.1 over 8s once on screen */}
        <div
          className="absolute top-1/2 left-1/2 h-full w-full"
          style={{
            transform: "translate(-50%, -50%) scale(1.0)",
            transformOrigin: "center",
            animation: hasBeenOnScreen
              ? "reviewImageScale 8s forwards 1 ease-out"
              : undefined,
          }}
        >
          <Picture
            src={image}
            alt={imageAlt}
            className="h-full w-full max-w-none object-cover"
          />
        </div>
      </div>
      {logo}
      {/* CustomP */}
      <p className="mt-4 text-[16px]">
        <span className="italic">&ldquo;{text}&rdquo;</span>
        {link ? (
          <>
            <br />
            {/* LinkMobile: inline-block below xsm, hidden at/above xsm */}
            <Link data-sid={`landing-review-link-mobile:${link}`}
              to={link}
              className="inline-block hover:no-underline min-[431px]:hidden"
            >
              Read review &rsaquo;
            </Link>
            {/* LinkDesktop: hidden below xsm, inline-block at/above xsm */}
            <Link data-sid={`landing-review-link-desktop:${link}`}
              to={link}
              target="_blank"
              className="hidden hover:no-underline min-[431px]:inline-block"
            >
              Read review &rsaquo;
            </Link>
          </>
        ) : null}
      </p>
    </div>
  );
}

export default function Reviews({
  carAndDriverImageAlt,
  forbesImageAlt,
  carAndDriverImage,
  forbesImage,
}: {
  carAndDriverImageAlt: string;
  forbesImageAlt: string;
  carAndDriverImage?: string;
  forbesImage?: string;
}) {
  const currentYear = new Date().getFullYear();

  return (
    <>
      {/* Component-local keyframe (original was an Emotion `keyframes`). */}
      <style>{`
        @keyframes reviewImageScale {
          0% { transform: translate(-50%, -50%) scale(1.0); }
          100% { transform: translate(-50%, -50%) scale(1.1); }
        }
      `}</style>
      <LandingPageHeaderH2 className="mb-12">
        Best Overall for {currentYear}
      </LandingPageHeaderH2>
      {/* ReviewGrid */}
      <div className="mb-8 grid grid-cols-1 gap-6 px-5 sm:px-0 md:grid-cols-2">
        <Review
          image={carAndDriverImage ?? womenDriving}
          imageAlt={carAndDriverImageAlt}
          logo={<CarAndDriverLogo width={200} />}
          text={
            <>
              Bouncie&rsquo;s effortless UX makes it our choice for the Best
              Overall GPS Tracker. Boasting a true plug-and-play setup,
              activating the device took mere moments. After a quick 10-minute
              drive, we were good to go.
            </>
          }
        />
        <Review
          image={forbesImage ?? beachFamily}
          imageAlt={forbesImageAlt}
          logo={<ForbesLogo width={129} />}
          text={
            <>
              The Bouncie remains on our list as one of the best all-around GPS
              trackers money can buy. It is perfect for people who want to
              monitor a driver&rsquo;s location, performance and safety.
            </>
          }
        />
      </div>
    </>
  );
}
